import { useState, useEffect, useCallback } from 'react';
import { printerWebSocket } from '../services/printerWebSocket';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';

export const usePrinterWebSocket = () => {
  const [status, setStatus] = useState('disconnected');
  const [clientStatus, setClientStatus] = useState('unknown');
  const [error, setError] = useState(null);
  const [foundPrinters, setFoundPrinters] = useState([]);
  const { business } = useBusiness();
  const { user } = useAuth();

  // Debug log para cambios de estado
  useEffect(() => {
    console.log('🔍 DEBUG ESTADO ACTUAL:', {
      wsStatus: status,
      clientStatus,
      foundPrinters,
      error: error?.message,
      component: 'usePrinterWebSocket',
      timestamp: new Date().toISOString()
    });
  }, [status, clientStatus, foundPrinters, error]);

  useEffect(() => {
    console.log('=== DEBUG USE PRINTER WEBSOCKET ===');
    console.log('Business:', {
      id: business?.id,
      name: business?.name,
      owner_id: business?.owner_id
    });

    const initializeWebSocket = async () => {
      if (!business?.id) {
        console.log('❌ No hay business_id, desconectando WebSocket...');
        printerWebSocket.disconnect();
        setStatus('disconnected');
        setError(new Error('Se requiere un negocio activo para usar las impresoras'));
        return;
      }

      const handleStatusChange = (newStatus) => {
        console.log('🔄 WebSocket status changed:', {
          oldStatus: status,
          newStatus,
          clientStatus // Debug: ver estado del cliente cuando cambia la conexión
        });
        setStatus(newStatus);
        setError(null);
      };

      const handleClientStatus = (data) => {
        console.log('🖨️ Cliente Printer:', {
          type: 'client_status',
          oldStatus: clientStatus,
          newStatus: data.status,
          receivedBusinessId: data.businessId,
          currentBusinessId: business.id,
          matches: data.businessId === business.id,
          component: 'handleClientStatus',
          timestamp: new Date().toISOString()
        });
        
        if (data.businessId === business.id) {
          setClientStatus(data.status);
          console.log('✅ Estado del cliente actualizado:', {
            newStatus: data.status,
            component: 'handleClientStatus',
            timestamp: new Date().toISOString()
          });
        }
      };

      const handlePrinterStatus = async (data) => {
        console.log('🖨️ Estado Individual Impresora:', {
          type: 'printer_status',
          printer: data.printer,
          businessMatches: data.printer.businessId === business.id
        });
        
        if (data.printer.businessId === business.id) {
          try {
            const { data: result, error: dbError } = await supabase
              .from('printers')
              .update({ 
                status: data.printer.status,
                last_seen: new Date().toISOString()
              })
              .eq('id', data.printer.id)
              .eq('business_id', business.id);

            console.log('📝 Actualización DB Individual:', {
              success: !dbError,
              error: dbError?.message,
              result
            });
          } catch (err) {
            console.error('❌ Error actualizando impresora:', err);
          }
        }
      };

      const handlePrintersStatus = async (data) => {
        console.log('🖨️ Estado Múltiple Impresoras:', {
          type: 'printers_status',
          printersCount: data.printers?.length,
          printers: data.printers
        });
        
        if (!data.printers?.length) return;

        try {
          // 1. Marcar todas como offline
          const { error: offlineError } = await supabase
            .from('printers')
            .update({ 
              status: 'offline',
              last_seen: new Date().toISOString()
            })
            .eq('business_id', business.id);

          console.log('📝 Marcar Offline:', {
            success: !offlineError,
            error: offlineError?.message
          });

          // 2. Actualizar las online
          const onlinePrinters = data.printers
            .filter(p => p.businessId === business.id && p.status === 'online')
            .map(p => p.id);

          console.log('📝 Impresoras Online:', {
            count: onlinePrinters.length,
            ids: onlinePrinters
          });

          if (onlinePrinters.length > 0) {
            const { error: onlineError } = await supabase
              .from('printers')
              .update({ 
                status: 'online',
                last_seen: new Date().toISOString()
              })
              .in('id', onlinePrinters)
              .eq('business_id', business.id);

            console.log('📝 Marcar Online:', {
              success: !onlineError,
              error: onlineError?.message
            });
          }
        } catch (err) {
          console.error('❌ Error actualizando impresoras:', err);
        }
      };

      const handleError = (err) => {
        console.error('❌ Error en WebSocket:', err);
        setError(err);
      };

      const handlePrinterFound = (data) => {
        // Nuevo formato: { type: 'printer_found', printer: { ip_address, name?, brand? } }
        console.log('🔍 Impresora encontrada:', data);
        setFoundPrinters(prev => {
          const exists = prev.some(p => p.ip_address === data.printer.ip_address);
          if (exists) return prev;
          return [...prev, data.printer];
        });
      };

      // Suscribirse a eventos
      printerWebSocket.on('statusChange', handleStatusChange);
      printerWebSocket.on('client_status', handleClientStatus);
      printerWebSocket.on('printer_status', handlePrinterStatus);
      printerWebSocket.on('printers_status', handlePrintersStatus);
      printerWebSocket.on('printer_found', handlePrinterFound);
      printerWebSocket.on('error', handleError);

      // Conectar y registrarse
      try {
        console.log('🔌 Conectando WebSocket...');
        printerWebSocket.connect(business.id);
        
        // Obtener primera impresora configurada
        const { data: printers } = await supabase
          .from('printers')
          .select('id')
          .eq('business_id', business.id)
          .limit(1)
          .single();

        if (!printers?.id) {
          console.log('⚠️ No hay impresoras configuradas');
          return;
        }

        // Enviar registro con el ID de la impresora
        console.log('📝 Enviando registro:', {
          type: 'register',
          printerId: printers.id,
          businessId: business.id
        });

        printerWebSocket.send({
          type: 'register',
          printerId: printers.id,
          businessId: business.id
        });
      } catch (err) {
        console.error('❌ Error conectando WebSocket:', err);
        setError(err);
      }

      return () => {
        printerWebSocket.off('statusChange', handleStatusChange);
        printerWebSocket.off('client_status', handleClientStatus);
        printerWebSocket.off('printer_status', handlePrinterStatus);
        printerWebSocket.off('printers_status', handlePrintersStatus);
        printerWebSocket.off('printer_found', handlePrinterFound);
        printerWebSocket.off('error', handleError);
        printerWebSocket.disconnect();
      };
    };

    initializeWebSocket();
  }, [business?.id]);

  const scanNetwork = useCallback(() => {
    if (!business?.id) return;
    setFoundPrinters([]); // Reset found printers
    printerWebSocket.send({
      type: 'scan_network',
      businessId: business.id
    });
  }, [business?.id]);

  return {
    status,
    clientStatus,
    error,
    foundPrinters,
    scanNetwork
  };
}; 